/**
 * Error tracking abstraction layer.
 *
 * This module provides an interface for error tracking across the application.
 * Sentry was previously used but was removed to eliminate third-party tracking.
 * This wrapper maintains all existing error tracking, without functionality,
 * making it easy to re-enable tracking by implementing these functions
 * with any error tracking service without needing to modify code throughout the application.
 *
 */

export interface ErrorTrackingOptions {
    extra?: Record<string, any>
    tags?: Record<string, string>
    user?: Record<string, any>
    level?: 'error' | 'warning' | 'info' | 'debug'
}

/**
 * Captures an exception for tracking.
 * Currently a no-op function that logs to console in development.
 *
 * @param error - The error to capture (Error object, string, or unknown from catch blocks)
 * @param options - Additional context and metadata
 */
export function captureException(error: unknown, options?: ErrorTrackingOptions): void {
    // No-op in production, but log in development for debugging
    if (process.env.NODE_ENV === 'development') {
        console.error('Error captured:', error, options)
    }

    // When re-enabling tracking, uncomment and implement:
    // import { captureException as sentryCaptureException } from '@sentry/nextjs'
    // return sentryCaptureException(error, options)
}
